import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Home, Building2, Briefcase, Globe, Leaf, ArrowRight, Star, CheckCircle, Phone, MessageCircle, ChevronLeft, ChevronRight, Zap, Recycle, TrendingUp, Users } from 'lucide-react';
import { TrustBadges } from '../components/TrustBadges';
import { CTAStrip } from '../components/CTAStrip';
const heroSlides = [{
  url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1600&auto=format&fit=crop',
  alt: 'Waste collection truck'
}, {
  url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1600&auto=format&fit=crop',
  alt: 'Construction waste clearance'
}, {
  url: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1600&auto=format&fit=crop',
  alt: 'Office removal team'
}, {
  url: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1600&auto=format&fit=crop',
  alt: 'House moving boxes'
}];
const services = [{
  icon: Trash2,
  label: 'Waste Disposal',
  desc: 'Licensed, eco-conscious waste removal',
  href: '/services/commercial-waste'
}, {
  icon: Home,
  label: 'House Moves',
  desc: 'Local & long-distance residential removals',
  href: '/services/house-clearance'
}, {
  icon: Building2,
  label: 'Clearance Services',
  desc: 'Full property & office clearances',
  href: '/services/office-removals'
}, {
  icon: Briefcase,
  label: 'Commercial Contracts',
  desc: 'Scheduled collections for businesses',
  href: '/contracts'
}, {
  icon: Globe,
  label: 'Overseas Moves',
  desc: 'International removals & packing crates',
  href: '/services/overseas-removals'
}, {
  icon: Leaf,
  label: 'Garden Waste',
  desc: 'Green waste, soil & garden clearances',
  href: '/services/garden-waste'
}];
const steps = [{
  num: '01',
  icon: MessageCircle,
  title: 'Tell Us What You Need',
  desc: 'Fill in our quick form or WhatsApp us with details about your job. No obligation.'
}, {
  num: '02',
  icon: CheckCircle,
  title: 'Get a Quick Quote',
  desc: 'Transparent pricing, no hidden fees. We reply within 30 minutes, guaranteed.'
}, {
  num: '03',
  icon: Recycle,
  title: 'We Collect & Dispose Responsibly',
  desc: 'Recycling-first approach. Fully documented, eco-conscious disposal every time.'
}];
const featuredServices = [{
  title: 'House Clearance',
  desc: 'Full or partial property clearances handled with care and efficiency.',
  img: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=600&auto=format&fit=crop',
  href: '/services/house-clearance'
}, {
  title: 'Office Removals',
  desc: 'Minimal disruption office moves and complete strip-outs.',
  img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&auto=format&fit=crop',
  href: '/services/office-removals'
}, {
  title: 'Commercial Waste',
  desc: 'Scheduled collections and one-off commercial waste removal.',
  img: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&auto=format&fit=crop',
  href: '/services/commercial-waste'
}, {
  title: 'Garden Waste',
  desc: 'Green waste, soil, branches and full garden clearances.',
  img: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&auto=format&fit=crop',
  href: '/services/garden-waste'
}];
const testimonials = [{
  name: 'Sarah M.',
  location: 'Chingford',
  rating: 5,
  text: "Absolutely brilliant service! They cleared my late mother's house with such care and professionalism. Everything was recycled where possible. Highly recommend."
}, {
  name: 'James T.',
  location: 'Enfield',
  rating: 5,
  text: 'Used Talk in Rubbish for our office clearance. Same-day service, competitive price, and the team were fantastic. Will definitely use again.'
}, {
  name: 'Priya K.',
  location: 'Walthamstow',
  rating: 5,
  text: 'Quick response, fair pricing, and they took everything including items I thought would be difficult. Great eco-friendly approach too.'
}];
const stats = [{
  icon: Recycle,
  value: '847',
  label: 'Tonnes Recycled This Year'
}, {
  icon: CheckCircle,
  value: '2,400+',
  label: 'Jobs Completed'
}, {
  icon: TrendingUp,
  value: '98%',
  label: 'Customer Satisfaction'
}, {
  icon: Users,
  value: '15+',
  label: 'Years Experience'
}];
export function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  return <div className="pt-16 lg:pt-20" data-id="element-606">
      {/* Hero */}
      <section className="relative h-[85vh] min-h-[560px] overflow-hidden" data-id="element-607">
        {heroSlides.map((slide, i) => <div key={i} className="absolute inset-0 transition-opacity duration-1000" style={{
        opacity: i === currentSlide ? 1 : 0
      }} data-id="element-608">
            <img src={slide.url} alt={slide.alt} className="w-full h-full object-cover" data-id="element-609" />
          </div>)}
        <div className="absolute inset-0 hero-overlay" data-id="element-610" />

        {/* Content */}
        <div className="relative z-10 h-full flex items-center" data-id="element-611">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-612">
            <div className="max-w-3xl" data-id="element-613">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-semibold mb-6" data-id="element-614">
                <Leaf className="w-4 h-4" style={{
                color: '#4ADE80'
              }} data-id="element-615" />
                Licensed Waste Carrier · Serving North & East London
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-5" style={{
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-616">
                Reliable Waste Disposal & Clearance Services
              </h1>
              <p className="text-lg md:text-xl text-green-100 mb-8 leading-relaxed max-w-2xl" data-id="element-617">
                Licensed waste carrier serving Chingford, North London, East
                London & Enfield. Fast, insured, eco-conscious collections for
                homes & businesses.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-618">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-white text-lg transition-all duration-200 hover:opacity-90 hover:shadow-xl" style={{
                backgroundColor: '#1B8F4B',
                fontFamily: 'Montserrat, sans-serif'
              }} data-id="element-619">
                  Get a Free Quote Today <ArrowRight className="w-5 h-5" data-id="element-620" />
                </Link>
                <a href="https://wa.me/447405641227" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-white text-lg border-2 border-white/60 hover:bg-white/10 transition-all duration-200 backdrop-blur-sm" style={{
                fontFamily: 'Montserrat, sans-serif'
              }} data-id="element-621">
                  <Phone className="w-5 h-5" data-id="element-622" /> Call / WhatsApp Now
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Carousel controls */}
        <button onClick={() => setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length)} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-colors" data-id="element-623">
          <ChevronLeft className="w-5 h-5" data-id="element-624" />
        </button>
        <button onClick={() => setCurrentSlide(prev => (prev + 1) % heroSlides.length)} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-colors" data-id="element-625">
          <ChevronRight className="w-5 h-5" data-id="element-626" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2" data-id="element-627">
          {heroSlides.map((_, i) => <button key={i} onClick={() => setCurrentSlide(i)} className="w-2.5 h-2.5 rounded-full transition-all duration-200" style={{
          backgroundColor: i === currentSlide ? '#fff' : 'rgba(255,255,255,0.4)'
        }} data-id="element-628" />)}
        </div>
      </section>

      {/* Trust Badges */}
      <TrustBadges data-id="element-629" />

      {/* Services Grid */}
      <section className="py-20 px-4 bg-white" data-id="element-630">
        <div className="max-w-7xl mx-auto" data-id="element-631">
          <div className="text-center mb-12" data-id="element-632">
            <p className="text-sm font-bold uppercase tracking-widest mb-2" style={{
            color: '#1B8F4B'
          }} data-id="element-633">
              What We Do
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-634">
              Our Services
            </h2>
            <p className="mt-3 text-gray-500 max-w-xl mx-auto" data-id="element-635">
              From single-item pick-ups to full commercial contracts — we handle
              it all, responsibly.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5" data-id="element-636">
            {services.map(({
            icon: Icon,
            label,
            desc,
            href
          }) => <Link key={label} to={href} className="group p-6 rounded-2xl border border-gray-100 hover:border-[#1B8F4B] hover:shadow-lg transition-all duration-300 bg-white" data-id="element-637">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300 group-hover:bg-[#1B8F4B]" style={{
              backgroundColor: '#F0FDF4'
            }} data-id="element-638">
                  <Icon className="w-6 h-6 transition-colors duration-300 group-hover:text-white" style={{
                color: '#1B8F4B'
              }} data-id="element-639" />
                </div>
                <h3 className="font-bold text-gray-900 mb-1" style={{
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-640">
                  {label}
                </h3>
                <p className="text-sm text-gray-500" data-id="element-641">{desc}</p>
              </Link>)}
          </div>
          <div className="text-center mt-8" data-id="element-642">
            <Link to="/services" className="inline-flex items-center gap-2 font-semibold hover:underline" style={{
            color: '#1B8F4B'
          }} data-id="element-643">
              View all services <ArrowRight className="w-4 h-4" data-id="element-644" />
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 texture-paper" data-id="element-645">
        <div className="max-w-7xl mx-auto" data-id="element-646">
          <div className="text-center mb-12" data-id="element-647">
            <p className="text-sm font-bold uppercase tracking-widest mb-2" style={{
            color: '#1B8F4B'
          }} data-id="element-648">
              Simple Process
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-649">
              How It Works
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-650">
            {steps.map(({
            num,
            icon: Icon,
            title,
            desc
          }) => <div key={num} className="relative text-center" data-id="element-651">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5 text-white font-bold text-xl" style={{
              backgroundColor: '#1B8F4B',
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-652">
                  {num}
                </div>
                <Icon className="w-8 h-8 mx-auto mb-3" style={{
              color: '#1B8F4B'
            }} data-id="element-653" />
                <h3 className="text-xl font-bold text-gray-900 mb-2" style={{
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-654">
                  {title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-655">{desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-20 px-4 bg-white" data-id="element-656">
        <div className="max-w-7xl mx-auto" data-id="element-657">
          <div className="text-center mb-12" data-id="element-658">
            <p className="text-sm font-bold uppercase tracking-widest mb-2" style={{
            color: '#1B8F4B'
          }} data-id="element-659">
              Popular Services
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-660">
              Featured Services
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-661">
            {featuredServices.map(({
            title,
            desc,
            img,
            href
          }) => <Link key={title} to={href} className="group rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300" data-id="element-662">
                <div className="relative h-48 overflow-hidden" data-id="element-663">
                  <img src={img} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-664" />
                  <div className="absolute top-0 left-0 w-full h-1" style={{
                backgroundColor: '#1B8F4B'
              }} data-id="element-665" />
                </div>
                <div className="p-5" data-id="element-666">
                  <h3 className="font-bold text-gray-900 mb-2" style={{
                fontFamily: 'Montserrat, sans-serif'
              }} data-id="element-667">
                    {title}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3" data-id="element-668">{desc}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{
                color: '#1B8F4B'
              }} data-id="element-669">
                    Learn More <ArrowRight className="w-3 h-3" data-id="element-670" />
                  </span>
                </div>
              </Link>)}
          </div>
        </div>
      </section>

      {/* Eco Tracker */}
      <section className="py-20 px-4" style={{
      background: 'linear-gradient(135deg, #1B8F4B 0%, #0F5D2E 100%)'
    }} data-id="element-671">
        <div className="max-w-7xl mx-auto" data-id="element-672">
          <div className="text-center mb-12" data-id="element-673">
            <p className="text-sm font-bold uppercase tracking-widest mb-2 text-green-300" data-id="element-674">
              Our Impact
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-675">
              Eco Tracker
            </h2>
            <p className="text-green-100 mt-2" data-id="element-676">
              Real numbers. Real impact. Every job contributes to a greener
              London.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-677">
            {stats.map(({
            icon: Icon,
            value,
            label
          }) => <div key={label} className="text-center" data-id="element-678">
                <Icon className="w-8 h-8 mx-auto mb-3 text-green-300" data-id="element-679" />
                <p className="text-4xl font-extrabold text-white mb-1" style={{
              fontFamily: 'Montserrat, sans-serif'
            }} data-id="element-680">
                  {value}
                </p>
                <p className="text-green-200 text-sm" data-id="element-681">{label}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-20 px-4 bg-gray-50" data-id="element-682">
        <div className="max-w-7xl mx-auto" data-id="element-683">
          <div className="text-center mb-12" data-id="element-684">
            <p className="text-sm font-bold uppercase tracking-widest mb-2" style={{
            color: '#1B8F4B'
          }} data-id="element-685">
              Customer Reviews
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-686">
              What Our Customers Say
            </h2>
            <div className="flex items-center justify-center gap-1 mt-3" data-id="element-687">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" style={{
              color: '#F59E0B'
            }} data-id="element-688" />)}
              <span className="ml-2 text-gray-600 font-semibold" data-id="element-689">
                4.9/5 from 200+ reviews
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-690">
            {testimonials.map(({
            name,
            location,
            rating,
            text
          }) => <div key={name} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-691">
                <div className="flex gap-1 mb-3" data-id="element-692">
                  {[...Array(rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" style={{
                color: '#F59E0B'
              }} data-id="element-693" />)}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-4" data-id="element-694">
                  "{text}"
                </p>
                <div data-id="element-695">
                  <p className="font-bold text-gray-900 text-sm" style={{
                fontFamily: 'Montserrat, sans-serif'
              }} data-id="element-696">
                    {name}
                  </p>
                  <p className="text-xs text-gray-400" data-id="element-697">{location}</p>
                </div>
              </div>)}
          </div>
          {/* Review platform badges */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-698">
            <div className="flex items-center gap-3 px-6 py-3 bg-white rounded-xl border border-gray-200 shadow-sm" data-id="element-699">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold" data-id="element-700">
                G
              </div>
              <div data-id="element-701">
                <p className="text-xs text-gray-500" data-id="element-702">Google Reviews</p>
                <div className="flex gap-0.5" data-id="element-703">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-current" style={{
                  color: '#F59E0B'
                }} data-id="element-704" />)}
                </div>
              </div>
              <span className="font-bold text-gray-800" data-id="element-705">4.9</span>
            </div>
            <div className="flex items-center gap-3 px-6 py-3 bg-white rounded-xl border border-gray-200 shadow-sm" data-id="element-706">
              <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white text-xs font-bold" data-id="element-707">
                T
              </div>
              <div data-id="element-708">
                <p className="text-xs text-gray-500" data-id="element-709">Trustpilot</p>
                <div className="flex gap-0.5" data-id="element-710">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-current" style={{
                  color: '#00B67A'
                }} data-id="element-711" />)}
                </div>
              </div>
              <span className="font-bold text-gray-800" data-id="element-712">Excellent</span>
            </div>
          </div>
        </div>
      </section>

      {/* Response Pledge */}
      <section className="py-12 px-4" style={{
      backgroundColor: '#F59E0B'
    }} data-id="element-713">
        <div className="max-w-4xl mx-auto text-center" data-id="element-714">
          <div className="flex items-center justify-center gap-3 mb-3" data-id="element-715">
            <Zap className="w-8 h-8 text-white" data-id="element-716" />
            <h2 className="text-2xl md:text-3xl font-extrabold text-white" style={{
            fontFamily: 'Montserrat, sans-serif'
          }} data-id="element-717">
              We Reply Within 30 Minutes
            </h2>
          </div>
          <p className="text-amber-100 text-lg mb-6" data-id="element-718">
            Get your free quote today — fast, friendly, no obligation.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-[#F59E0B] bg-white hover:bg-amber-50 transition-all duration-200" style={{
          fontFamily: 'Montserrat, sans-serif'
        }} data-id="element-719">
            Get a Free Quote <ArrowRight className="w-4 h-4" data-id="element-720" />
          </Link>
        </div>
      </section>

      <CTAStrip data-id="element-721" />
    </div>;
}